import React, { useMemo } from "react";
import { Formik, Form } from "formik";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";

import { PrimaryButton } from "widgets";
import {
  useCreateBranch,
  useFetchCountryList,
  useFetchGradeList,
  useFetchTenantDetails,
} from "store/backend/apiHooks";
import { useSnackbarContext } from "widgets/SnackBar/SnackBar";
import Loader from "shared/Icons/Loader/Loader";
import BranchForm from "components/BranchForm/BranchForm";
import routes from "constants/routes";
import {
  buildBranchPayload,
  BRANCH_FORM_INITIAL_VALUES,
  createBranchSchema,
} from "helpers/branchUtils";
import {
  getCountryOptions,
  mapGradesToOptions,
  resolveTenantUuid,
} from "helpers/tenantUtils";
import { SUCCESS_REDIRECT_SNACKBAR_DELAY_MS } from "constants/constants";

import styles from "./CreateBranch.module.scss";

const handleCreateSubmit = (
  values,
  countryOptions,
  tenantCountryId,
  createBranch,
  openSnackbar,
  navigate,
  tenantId,
  setSubmitting
) => {
  const payload = buildBranchPayload(
    values,
    countryOptions,
    tenantCountryId,
    null,
    tenantId,
  );
  createBranch(payload, {
    onSuccess: (response) => {
      setSubmitting(false);
      const branchId = response.data.branchId;
      navigate(`/branchDetails?branchId=${branchId}`);
      setTimeout(() => {
        openSnackbar("Branch created successfully!", "success");
      }, SUCCESS_REDIRECT_SNACKBAR_DELAY_MS);
    },
    onError: (error) => {
      setSubmitting(false);
      openSnackbar("Error creating branch: " + error.message, "error");
    },
  });
};

const CreateBranch = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const tenantId = searchParams.get("tenantId");
  const existingGradeIds = location.state?.gradeList;
  const { tenantName, tenantCode } = location.state || {};
  const { openSnackbar } = useSnackbarContext();
  const navigate = useNavigate();
  const { mutate: createBranch, isPending: isCreateBranchLoading } = useCreateBranch();
  const {
    data: gradeList,
    isPending: isGradeListLoading,
    isError: isErrorOnGradeList,
    error: errorMessageOnFetchGradeList,
  } = useFetchGradeList();
  const {
    data: tenantDetails,
    isPending: isTenantDetailsLoading,
  } = useFetchTenantDetails(tenantId);
  const {
    data: countryList,
    isPending: isCountryListLoading,
  } = useFetchCountryList();

  const filteredGrades =
    gradeList?.items?.filter((grade) => existingGradeIds?.includes(grade.id)) || [];
  const gradeOptions = mapGradesToOptions(filteredGrades);
  const countryOptions = useMemo(
    () => (countryList?.items ? getCountryOptions(countryList.items) : []),
    [countryList],
  );
  const tenantCountryId =
    tenantDetails?.countryId || tenantDetails?.country_id || "";
  const branchSchema = useMemo(
    () => createBranchSchema(countryOptions, tenantCountryId),
    [countryOptions, tenantCountryId],
  );
  const isFormContextLoading =
    isTenantDetailsLoading || isCountryListLoading || !tenantCountryId;

  const tenantDetailsUrl = tenantId
    ? `${routes.tenantDetails.tenantDetails}?tenantId=${tenantId}`
    : routes.tenants.tenants;

  return (
    <div className={styles.mainContainer}>
      <header className={styles.pageHeader}>
        <div className={styles.headerTop}>
          <Link className={styles.backLink} to={tenantDetailsUrl}>
            ← Back to tenant
          </Link>
          <h1 className={styles.pageTitle}>Create branch</h1>
          <div className={styles.headerSpacer} aria-hidden="true" />
        </div>
      </header>
      {tenantName ? (
          <p className={styles.tenantMeta}>
            <span className={styles.tenantMetaLabel}>Tenant</span>
            {tenantName}
            {tenantCode ? (
              <span className={styles.tenantMetaCode}>({tenantCode})</span>
            ) : null}
          </p>
        ) : null}

      <div className={styles.formWrap}>
        {isFormContextLoading ? (
          <Loader />
        ) : (
          <Formik
            initialValues={BRANCH_FORM_INITIAL_VALUES}
            validationSchema={branchSchema}
            validateOnMount={false}
            validateOnChange={true}
            validateOnBlur={true}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(true);
              handleCreateSubmit(
                values,
                countryOptions,
                tenantCountryId,
                createBranch,
                openSnackbar,
                navigate,
                resolveTenantUuid(tenantDetails, tenantId),
                setSubmitting
              );
            }}
          >
            {({
              values,
              errors,
              touched,
              dirty,
              setFieldValue,
              setFieldTouched,
              handleBlur,
              handleSubmit,
              isSubmitting,
              submitCount,
            }) => (
              <Form onSubmit={handleSubmit}>
                <BranchForm
                  values={values}
                  touched={touched}
                  dirty={dirty}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  handleBlur={handleBlur}
                  errors={errors}
                  submitCount={submitCount}
                  gradeOptions={gradeOptions}
                  countryOptions={countryOptions}
                  tenantCountryId={tenantCountryId}
                  isGradeListLoading={isGradeListLoading}
                  isErrorOnGradeList={isErrorOnGradeList}
                  errorMessageOnFetchGradeList={errorMessageOnFetchGradeList}
                />
                <div className={styles.submitRow}>
                  <PrimaryButton
                    type="submit"
                    disabled={isSubmitting || isCreateBranchLoading}
                  >
                    {isCreateBranchLoading || isSubmitting ? (
                      <Loader />
                    ) : (
                      "Create branch"
                    )}
                  </PrimaryButton>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

export default CreateBranch;
